import { useVersionCheck } from '@src/hooks/useVersionCheck';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import React, { useCallback, useState } from 'react';

import RefreshNotification from './RefreshNotification';

interface VersionCheckerProps {
  checkInterval?: number;
  enabled?: boolean;
}

const VersionChecker: React.FC<VersionCheckerProps> = ({
  checkInterval = 5 * 60 * 1000, // 5 minutes default
  enabled = true,
}) => {
  const [isDismissed, setIsDismissed] = useState(false);
  const videoCreatorStore = useVideoCreatorStore();

  const handleUpdateAvailable = useCallback((latestVersion: string) => {
    console.log('New version available:', latestVersion);
    // Reset dismissed state when a new version is detected
    setIsDismissed(false);
  }, []);

  const hookResult = useVersionCheck({
    checkInterval,
    enabled,
    onUpdateAvailable: handleUpdateAvailable,
  });

  // Defensive check to prevent destructuring errors in test environments
  if (!hookResult) {
    return null;
  }

  const {
    isUpdateAvailable,
    latestVersion,
    refreshPage,
    currentVersion,
    isChecking,
    error,
    forceReloadEnabled,
  } = hookResult;

  const handleRefresh = useCallback(async () => {
    // Save the current work before refreshing, but only if there are unsaved changes
    if (videoCreatorStore?.saveStoryAndVideo) {
      try {
        await videoCreatorStore.saveStoryAndVideo();
      } catch (error) {
        console.error('Failed to save before refresh:', error);
      }
    }
    refreshPage();
  }, [refreshPage, videoCreatorStore]);

  const handleDismiss = useCallback(() => {
    setIsDismissed(true);
  }, []);

  // Handle force reload when update is available
  React.useEffect(() => {
    if (isUpdateAvailable && forceReloadEnabled && !isDismissed) {
      console.log('Force reload enabled - automatically refreshing page');
      handleRefresh();
    }
  }, [isUpdateAvailable, forceReloadEnabled, isDismissed, handleRefresh]);

  // Log version check status for debugging
  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Version Check Status:', {
        currentVersion,
        latestVersion,
        isUpdateAvailable,
        isChecking,
        error,
        isDismissed,
      });
    }
  }, [
    currentVersion,
    latestVersion,
    isUpdateAvailable,
    isChecking,
    error,
    isDismissed,
  ]);

  // Don't show notification if dismissed, no update available, or force reload is enabled
  const shouldShowNotification =
    isUpdateAvailable && !isDismissed && !forceReloadEnabled;

  return (
    <RefreshNotification
      isVisible={shouldShowNotification}
      onRefresh={handleRefresh}
      onDismiss={handleDismiss}
    />
  );
};

export default VersionChecker;
